import HttpApi from "./HttpApi";
import { DepositStatus } from "./TbtcApi";
export function buildGetDepositsByOwnerQuery(owner) {
    return `
  query {
    deposits(
          where: {depositOwner_: {id: "0x${owner.identifierHex}"}}
      ) {
          id
          bitcoinTransactionId
          initialDepositAmount
          events(orderBy: timestamp, orderDirection: asc) {
            timestamp
            type
          }
          amountToDeposit
      }
  }`;
}
export function buildGetWithdrawalsByOwnerQuery(owner) {
    return `
  query {
    withdraws(
      where: {depositOwner_contains_nocase: "0x${owner.identifierHex}"}
    ) {
        id
        bitcoinTransactionId
        requestedAmount
        amountToRedeem
        amount
        events(orderBy: timestamp, orderDirection: asc) {
          timestamp
          type
        }
      }
  }`;
}
/**
 * Class for integration with Acre Subgraph.
 */
export default class AcreSubgraphApi extends HttpApi {
    /**
     * @param depositOwnerId The deposit owner id as EVM-chain identifier.
     * @returns All deposits for a given depositor. Returns only initialized or
     *          finalized deposits that exist on-chain. They do not included
     *          queued deposits stored by the tBTC API.
     */
    async getDepositsByOwner(depositOwnerId) {
        const query = buildGetDepositsByOwnerQuery(depositOwnerId);
        const response = await this.postRequest("", { query }, { credentials: undefined });
        if (!response.ok) {
            throw new Error(`Could not get deposits by deposit owner: ${response.status}`);
        }
        const responseData = (await response.json());
        return responseData.data.deposits.map((deposit) => {
            const { bitcoinTransactionId: txHash, amountToDeposit, initialDepositAmount, events, id, } = deposit;
            // The subgraph indexes only initialized, finalized or migrated deposits.
            let status = DepositStatus.Initialized;
            if (events.some(({ type }) => type === "Finalized"))
                status = DepositStatus.Finalized;
            if (events.some(({ type }) => type === "Migrated"))
                status = DepositStatus.Migrated;
            const [initializedEvent, finalizedEvent] = events;
            const initializedAt = parseInt(initializedEvent.timestamp, 10);
            const finalizedAt = finalizedEvent
                ? parseInt(finalizedEvent.timestamp, 10)
                : undefined;
            return {
                depositKey: id,
                txHash,
                initialAmount: BigInt(initialDepositAmount),
                amountToDeposit: BigInt(amountToDeposit ?? 0),
                type: "deposit",
                status,
                initializedAt,
                finalizedAt,
            };
        });
    }
    async getWithdrawalsByOwner(depositOwnerId) {
        const query = buildGetWithdrawalsByOwnerQuery(depositOwnerId);
        const response = await this.postRequest("", { query }, { credentials: undefined });
        if (!response.ok) {
            throw new Error(`Could not get withdrawals by deposit owner: ${response.status}`);
        }
        const acreWithdrawals = (await response.json());
        return acreWithdrawals.data.withdraws.map((withdraw) => {
            const { id, events } = withdraw;
            const bitcoinTransactionId = withdraw.bitcoinTransactionId ?? undefined;
            const requestedAmount = BigInt(withdraw.requestedAmount);
            // The `amount` field from subgraph is available once the bridging process
            // on the tBTC side has finished. If it's not available let's use the
            // `amountToRedeem` field, available immediately after withdrawal request
            // initialization.
            const amount = withdraw.amount
                ? BigInt(withdraw.amount)
                : BigInt(withdraw.amountToRedeem);
            const [requestedEvent, initializedEvent, finalizedEvent] = events;
            const requestedAt = parseInt(requestedEvent.timestamp, 10);
            const initializedAt = initializedEvent
                ? parseInt(initializedEvent.timestamp, 10)
                : undefined;
            const finalizedAt = finalizedEvent
                ? parseInt(finalizedEvent.timestamp, 10)
                : undefined;
            return {
                id,
                bitcoinTransactionId,
                amount,
                requestedAmount,
                requestedAt,
                initializedAt,
                finalizedAt,
            };
        });
    }
}
//# sourceMappingURL=AcreSubgraphApi.js.map